import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { MainHeadingText, SecondaryHeadingText, BodySmallText, BodyBigBoldText, BodyBigRegularText } from '../../../../styles/Text';
import { ChooseDateComponent } from './ui/CreateNewTask/ChooseDate/ChooseDateComponent';
import { ChooseDurationComponent } from './ui/CreateNewTask/ChooseDuration/ChooseDurationComponent';
import { ChooseMinPointsComponent } from './ui/CreateNewTask/ChooseMinPoints/ChooseMinPointsComponent';
import { convertMinutesToDurationFormat } from '../../../../utils/convertMinutesToDurationFormat';
import { convertDatesToDurationFormat } from '../../../../utils/convertDatesToDurationFormat';
import { EventType } from '../../../../core/types/event.interface';
import { TaskFormInput } from '../../../common/main/form/TaskFormInput';
import { BlueButton } from '../../../common/main/button/BlueButton';
import { SelectStyled } from '../../../common/main/form/TaskFormSelect';
import { TaskFormTextAreaInput } from '../../../common/main/form/TaskFormTextArea';
import { eventApi } from '../../../../core/api/rtk-query/event.api';
import { groupApi } from '../../../../core/api/rtk-query/group.api';
import { userApi } from '../../../../core/api/rtk-query/user.api';
import { daysFromToInitialValue } from '../../../../utils/daysFromToInitialValue';
import { ChooseTask } from './ui/ChooseTask';
import { IProblem } from '../../../../core/types/problem.interface';
import {
  CreateTaskFormStyled,
  CountSymbols,
  ButtonStyled,
  CreateTaskFormContainer,
  CreateTaskFormField,
  RadioButtonStyled,
  AssigneeContainerStyled,
} from './ui/CreateNewTask/Styles';

dayjs.extend(utc);

const StyledPageContent = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: flex-start;
  gap: 57px;
  flex: 1 0 0;
  align-self: stretch;
`;

const RightLine = styled.div`
  border-right: 1px solid #e1e1e1;
  height: 100%;
`;

interface ICreateTaskFields {
  name: string;
  type: EventType;
  description: string;
}

const universityEventTypesDecriptions: { name: EventType; label: string }[] = [
  {
    name: EventType.Homework,
    label: 'Домашнее задание',
  },
  {
    name: EventType.Contest,
    label: 'Контрольная работа',
  },
  {
    name: EventType.Milestone,
    label: 'Рубежная работа',
  },
  {
    name: EventType.Exam,
    label: 'Экзамен',
  },
];

export const CreateTaskPage = () => {
  const navigate = useNavigate();
  const [selectedTasks, setSelectedTasks] = useState<IProblem[]>([]);
  const [createEvent] = eventApi.useCreateEventMutation();
  const [grantEventAccessForGroup] = eventApi.useGrantEventAccessForGroupMutation();
  const [addEventToUsers] = eventApi.useAddEventToUsersMutation();

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ICreateTaskFields>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      type: EventType.Homework,
      description: '',
    },
  });

  const [eventDates, setEventDates] = useState({
    startTime: daysFromToInitialValue.startTimeInitialValueISO,
    endTime: daysFromToInitialValue.endTimeInitialValueISO,
  });

  const [minimumPoints, setMinimumPoints] = useState<number>(5);
  const isHomework = getValues('type') === EventType.Homework;

  const [eventDuration, setEventDuration] = useState<string>(
    convertDatesToDurationFormat(eventDates.startTime, eventDates.endTime),
  );

  const [selectedAssignee, setSelectedAssignee] = useState<'group' | 'student' | null>('group');
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedStudents, setSelectedStudents] = useState<any[]>([]);
  const [studentSearch, setStudentSearch] = useState<string>('');
  const { data: groupActiveData } = groupApi.useGetActiveGroupQuery();
  const [triggerSearchUsers, { data: searchResults }] = userApi.useLazySearchUsersQuery();

  const handleDatesChange = (dates: { fromDate: string; fromTime: string; toDate: string; toTime: string }) => {
    const startTime = `${dates.fromDate}T${dates.fromTime}:00`;
    const endTime = `${dates.toDate}T${dates.toTime}:00`;
    setEventDates({ startTime, endTime });
    setEventDuration(convertDatesToDurationFormat(startTime, endTime));
  };

  const handleDurationChange = (minutes: number) => {
    setEventDuration(convertMinutesToDurationFormat(minutes));
  };

  const handleMinPointsChange = (points: number) => {
    setMinimumPoints(points);
  };

  const onSubmit: SubmitHandler<ICreateTaskFields> = async (data) => {
    try {
      // Создаем мероприятие
      const problemCreationRequests = selectedTasks
        .filter(task => task.hash && task.hash.trim() !== '') // Фильтруем задачи без hash
        .map(task => ({
          hash: task.hash,  // Backend ожидает hash, не problemId!
          points: task.points || 10
        }));

      const payload: any = {
        name: data.name.trim(),
        description: data.description.trim(),
        type: data.type,
        startTime: eventDates.startTime,
        endTime: eventDates.endTime,
        duration: eventDuration,
        minimumPointsToPass: minimumPoints,
        problemCreationRequests,
        // Добавляем groupId если выбрана группа
        groupId: selectedAssignee === 'group' && selectedGroup ? selectedGroup : null,
      };

      const createdEvent = await createEvent(payload).unwrap();

      // Выдаем доступ группе (добавляем студентов в users_events)
      if (selectedAssignee === 'group' && selectedGroup) {
        await grantEventAccessForGroup({ eventId: createdEvent.id, groupId: selectedGroup }).unwrap();
      } else if (selectedAssignee === 'student' && selectedStudents.length > 0) {
        const userIds = selectedStudents.map(s => s.id);
        await addEventToUsers({ id: createdEvent.id, userIds }).unwrap();
      }

      toast.success('Мероприятие создано успешно!');
      navigate('/teacher/events');
    } catch (error: any) {
      console.error('Error creating event:', error);
      toast.error(error?.data?.message || 'Ошибка при создании мероприятия');
    }
  };

  return (
    <>
      <MainHeadingText style={{ marginBottom: '15px' }}>Создание мероприятия</MainHeadingText>

      <StyledPageContent>
        <RightLine>
          <div>
            <SecondaryHeadingText style={{ marginBottom: '15px' }}>О мероприятии</SecondaryHeadingText>
            <CreateTaskFormStyled onSubmit={handleSubmit(onSubmit)}>
              <CreateTaskFormContainer>
                <CreateTaskFormField>
                  <label>
                    <BodyBigBoldText>
                      Название <span style={{ color: '#F3163E' }}>*</span>
                    </BodyBigBoldText>
                  </label>
                  <TaskFormInput
                    {...register('name', { required: true, minLength: 5, maxLength: 50 })}
                    placeholder="Введите название мероприятия"
                    maxLength={50}
                    minLength={5}
                  />
                </CreateTaskFormField>

                <CreateTaskFormField>
                  <label>
                    <BodyBigBoldText>
                      Тип <span style={{ color: '#F3163E' }}>*</span>
                    </BodyBigBoldText>
                  </label>
                  <SelectStyled {...register('type', { required: true })}>
                    {universityEventTypesDecriptions.map(o => (
                      <option key={o.name} value={o.name}>
                        {o.label}
                      </option>
                    ))}
                  </SelectStyled>
                </CreateTaskFormField>

                <CreateTaskFormField>
                  <label>
                    <BodyBigBoldText>Описание</BodyBigBoldText>
                  </label>
                  <TaskFormTextAreaInput
                    {...register('description', { maxLength: 300 })}
                    placeholder="Введите описание мероприятия"
                    maxLength={300}
                    minLength={5}
                  />
                  <CountSymbols>
                    <BodySmallText
                      style={{
                        color: errors.description?.type === 'maxLength' ? 'red' : '#787676',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {watch('description').length}/300
                    </BodySmallText>
                  </CountSymbols>
                  <ChooseMinPointsComponent selectedTasks={selectedTasks} onMinPointsChange={handleMinPointsChange} />
                </CreateTaskFormField>

                <CreateTaskFormField>
                  <AssigneeContainerStyled>
                    <BodyBigBoldText>
                      Назначить <span style={{ color: '#F3163E' }}>*</span>
                    </BodyBigBoldText>

                    <RadioButtonStyled>
                      <TaskFormInput
                        type="radio"
                        name="assignee"
                        value="group"
                        checked={selectedAssignee === 'group'}
                        onChange={() => setSelectedAssignee('group')}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <label>
                        <BodyBigRegularText>Курс</BodyBigRegularText>
                      </label>
                    </RadioButtonStyled>

                    {selectedAssignee === 'group' && (
                      <>
                        <SelectStyled value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)}>
                          <option value="">Выберите курс...</option>
                          {groupActiveData?.map(o => (
                            <option key={o.id} value={o.id}>
                              {o.name}
                            </option>
                          ))}
                        </SelectStyled>
                        <ChooseDateComponent onDatesChange={handleDatesChange} />
                      </>
                    )}

                    <RadioButtonStyled>
                      <TaskFormInput
                        type="radio"
                        name="assignee"
                        value="student"
                        checked={selectedAssignee === 'student'}
                        onChange={() => setSelectedAssignee('student')}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <label>
                        <BodyBigRegularText>Студент</BodyBigRegularText>
                      </label>
                    </RadioButtonStyled>

                    {selectedAssignee === 'student' && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ marginBottom: 12 }}>
                          <TaskFormInput
                            placeholder="Поиск студентов по имени или ISU..."
                            value={studentSearch}
                            onChange={(e: any) => {
                              setStudentSearch(e.target.value);
                              if (e.target.value.length >= 2) {
                                triggerSearchUsers({ name: e.target.value, pageNumber: 1, pageSize: 20 });
                              }
                            }}
                            style={{ width: '100%', marginBottom: 8 }}
                          />
                          {searchResults?.users && searchResults.users.length > 0 && studentSearch.length >= 2 && (
                            <div style={{ border: '1px solid #dbe3f6', borderRadius: 8, maxHeight: 200, overflowY: 'auto', background: '#fff' }}>
                              {searchResults.users.map((user: any) => (
                                <div
                                  key={user.id}
                                  onClick={() => {
                                    if (!selectedStudents.find(s => s.id === user.id)) {
                                      setSelectedStudents([...selectedStudents, user]);
                                    }
                                    setStudentSearch('');
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: 14
                                  }}
                                  onMouseEnter={(e: any) => e.currentTarget.style.background = '#f7f9ff'}
                                  onMouseLeave={(e: any) => e.currentTarget.style.background = '#fff'}
                                >
                                  {user.name} ({user.email}) {user.isuNumber ? `- ISU: ${user.isuNumber}` : ''}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {selectedStudents.length > 0 && (
                          <div>
                            <BodyBigRegularText style={{ marginBottom: 8 }}>Назначено студентам:</BodyBigRegularText>
                            {selectedStudents.map((student: any) => (
                              <div key={student.id} style={{ padding: '4px 0', fontSize: 14, color: '#667', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{student.name} ({student.email})</span>
                                <button
                                  type="button"
                                  onClick={() => setSelectedStudents(selectedStudents.filter(s => s.id !== student.id))}
                                  style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: 16 }}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </AssigneeContainerStyled>
                </CreateTaskFormField>
                {!isHomework && <ChooseDurationComponent onDurationChange={handleDurationChange} />}
              </CreateTaskFormContainer>
              <ButtonStyled>
                <BlueButton type="submit" style={{ width: '200px', height: '40px' }}>
                  Создать мероприятие
                </BlueButton>
              </ButtonStyled>
            </CreateTaskFormStyled>
          </div>
        </RightLine>

        <div>
          <SecondaryHeadingText style={{ marginBottom: '15px' }}>Просмотр задач</SecondaryHeadingText>
          <ChooseTask selectTasks={setSelectedTasks} selectedTasks={selectedTasks} />
        </div>
      </StyledPageContent>
    </>
  );
};
